import React, { useMemo } from 'react';
import type { Campaign, PCAFrameDetails, PCAReportMetric } from 'c-sdk';
import { PCAReportField } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import { useEntityData } from 'c-data';
import { campaignShowsUnitId } from 'c-main/Lib';
import { Box } from '@mui/material';
import FrameTable from './FrameTable';
import DspFrameTable from './DspFrameTable';

type Props = {
    campaignId: number;
    field: string;
    frames: PCAFrameDetails[];
    onSelect: (frameId: string) => void;
    metric: PCAReportMetric;
    comparisonHeaderLabel?: string;
    geopathOnly?: boolean;
    percentageHeaderLabel?: string;
    showActualBeforeComparison?: boolean;
    actualHeaderLabel?: string;
};

const FilterableFrameList: React.FC<Props> = ({
    campaignId,
    field,
    frames: rawFrames,
    onSelect,
    metric,
    comparisonHeaderLabel,
    geopathOnly = false,
    percentageHeaderLabel,
    showActualBeforeComparison = false,
    actualHeaderLabel,
}) => {
    const t = useCommonTranslation();
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id: campaignId });
    const showUnitId = campaignShowsUnitId(campaign);
    const isDsp =
        campaign?.type != null && campaign.type !== 'linear' && campaign.type !== 'default';

    // Programmatic campaigns have no separate unit id; show the frame id in its place. The API
    // does the same for new CADs, this also covers ones built before the column existed.
    const frames = useMemo(
        () =>
            showUnitId && isDsp
                ? rawFrames.map(frame => ({ ...frame, unitId: frame.unitId || frame.frameId }))
                : rawFrames,
        [rawFrames, showUnitId, isDsp],
    );

    const usableFields = useMemo(
        () =>
            [
                // PCAReportField.Town,
                PCAReportField.TvRegion,
                // PCAReportField.Conurbation,
                PCAReportField.Environment,
                PCAReportField.Format,
                PCAReportField.MediaOwner,
                PCAReportField.LineItemName,
                // PCAReportField.SurfaceArea,
                // PCAReportField.Resolution,
                PCAReportField.FrameId,
                ...(showUnitId ? [PCAReportField.UnitId] : []),
            ].filter(f => f !== field),
        [field, showUnitId],
    );
    const groupHeaders = useMemo(
        () =>
            usableFields.reduce((acc, field) => {
                acc[field] = t(`Modules.Main.Campaigns.Overview.Reports.fields.${field}`);
                return acc;
            }, {} as Record<PCAReportField, string>),
        [t, usableFields],
    );
    const useDspFrameTable = Boolean(comparisonHeaderLabel || percentageHeaderLabel || geopathOnly);

    return (
        <Box flex={1} display="flex" flexDirection="column" overflow="hidden">
            <Box flex={1} overflow="hidden" display="flex">
                {useDspFrameTable ? (
                    <DspFrameTable
                        frames={frames}
                        columns={usableFields}
                        columnHeaders={groupHeaders}
                        onSelect={onSelect}
                        metric={metric}
                        comparisonHeaderLabel={comparisonHeaderLabel}
                        geopathOnly={geopathOnly}
                        percentageHeaderLabel={percentageHeaderLabel}
                        showActualBeforeComparison={showActualBeforeComparison}
                        actualHeaderLabel={actualHeaderLabel}
                    />
                ) : (
                    <FrameTable
                        frames={frames}
                        columns={usableFields}
                        columnHeaders={groupHeaders}
                        onSelect={onSelect}
                        metric={metric}
                    />
                )}
            </Box>
        </Box>
    );
};

export default FilterableFrameList;
